import React, { useEffect, useState, useMemo } from 'react';
import api from '../utils/api';

const PERSONA_FILTERS = [
  { value: '', label: 'All' },
  { value: 'Individual', label: 'Individual' },
  { value: 'SME / Startup', label: 'SME / Startup' },
  { value: 'Corporate', label: 'Corporate' },
  { value: 'Project Developer / NGO', label: 'Project Developer' },
  { value: 'Investor / Analyst', label: 'Investor / Analyst' },
  { value: 'Compliance Entity', label: 'Compliance Entity' },
];

const TAG_COLORS = {
  CCTS: 'var(--neon-orange)',
  Policy: 'var(--neon-cyan)',
  Compliance: 'var(--neon-yellow)',
  Offset: 'var(--neon-green)',
  VCM: 'var(--neon-pink)',
  Pricing: 'var(--neon-blue)',
  Legal: 'var(--neon-purple)',
  Research: 'var(--neon-cyan)',
  Explainer: 'var(--neon-green)',
  Investment: 'var(--neon-orange)',
  SME: 'var(--neon-yellow)',
  Global: 'var(--neon-blue)',
};

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function News() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ persona: '', tag: '', q: '' });
  const [allTags, setAllTags] = useState([]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter.persona) params.persona = filter.persona;
    if (filter.tag) params.tag = filter.tag;
    if (filter.q) params.q = filter.q;
    api.get('/news', { params })
      .then((r) => {
        setArticles(r.data.articles || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    api.get('/news/meta/tags').then((r) => setAllTags(r.data.tags || [])).catch(() => {});
  }, []);

  const featured = useMemo(() => articles.filter(a => a.featured), [articles]);
  const regular = useMemo(() => articles.filter(a => !a.featured), [articles]);

  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.8rem' }}>
        <div className="section-stack">
          <div className="section-tag" style={{ color: 'var(--neon-cyan)' }}>Market intelligence</div>
          <h1 className="section-title">India Carbon Market — News & Analysis</h1>
          <p className="section-copy">
            Policy updates, regulatory notifications, and expert analysis on India's CCTS and voluntary carbon markets — curated from official and reputable sources.
          </p>
        </div>

        <div className="metric-strip" style={{ marginTop: '1.5rem' }}>
          <div className="metric-tile">
            <div className="eyebrow">Articles indexed</div>
            <div className="value" style={{ color: 'var(--neon-green)' }}>{articles.length}</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Official sources</div>
            <div className="value" style={{ color: 'var(--neon-cyan)' }}>PIB, BEE, MoEFCC</div>
          </div>
          <div className="metric-tile">
            <div className="eyebrow">Coverage</div>
            <div className="value" style={{ color: 'var(--neon-orange)' }}>CCTS, VCM, Policy</div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="surface-panel filter-shell">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="stat-label" style={{ whiteSpace: 'nowrap' }}>For me:</div>
          <div className="filter-pills">
            {PERSONA_FILTERS.map(({ value, label }) => (
              <button
                key={value || 'all'}
                type="button"
                className={`filter-pill ${filter.persona === value ? 'active' : ''}`}
                onClick={() => setFilter((prev) => ({ ...prev, persona: value }))}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {allTags.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem', alignItems: 'center' }}>
            <div className="stat-label" style={{ whiteSpace: 'nowrap' }}>Topic:</div>
            <button
              type="button"
              className={`filter-pill ${filter.tag === '' ? 'active' : ''}`}
              onClick={() => setFilter((prev) => ({ ...prev, tag: '' }))}
            >All</button>
            {allTags.map(tag => (
              <button
                key={tag}
                type="button"
                className={`filter-pill ${filter.tag === tag ? 'active' : ''}`}
                onClick={() => setFilter((prev) => ({ ...prev, tag: prev.tag === tag ? '' : tag }))}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        <div className="search-shell" style={{ marginTop: '0.75rem' }}>
          <input
            className="form-input"
            type="text"
            placeholder="Search articles..."
            value={filter.q}
            onChange={(e) => setFilter((prev) => ({ ...prev, q: e.target.value }))}
          />
        </div>
      </section>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : (
        <>
          {/* Featured articles */}
          {featured.length > 0 && (
            <section className="section-stack">
              <div className="section-tag">Featured</div>
              <div className="section-grid-2">
                {featured.map((article) => (
                  <a
                    key={article.id}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="surface-panel glow-surface"
                    style={{ padding: '1.5rem', textDecoration: 'none', display: 'grid', gap: '0.8rem', borderTop: '2px solid var(--neon-cyan)', cursor: 'pointer' }}
                  >
                    <div>
                      <div className="stat-label" style={{ marginBottom: '0.35rem' }}>{article.source}</div>
                      <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1rem', fontWeight: 700, color: 'var(--text)', lineHeight: '1.45' }}>{article.title}</div>
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: '0.84rem', lineHeight: '1.7' }}>{article.summary}</div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {(article.tags || []).slice(0, 3).map(tag => (
                          <span key={tag} className="badge" style={{ color: TAG_COLORS[tag] || 'var(--muted2)', borderColor: 'transparent', background: 'rgba(255,255,255,0.04)', fontSize: '0.62rem' }}>{tag}</span>
                        ))}
                      </div>
                      <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.62rem', color: 'var(--muted2)' }}>{formatDate(article.published_at)}</div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Regular articles */}
          {regular.length > 0 && (
            <section className="section-stack">
              {featured.length > 0 && <div className="section-tag">All articles</div>}
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {regular.map((article) => (
                  <a
                    key={article.id}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="surface-panel"
                    style={{ padding: '1.2rem 1.5rem', textDecoration: 'none', display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'start' }}
                  >
                    <div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                        <div className="stat-label">{article.source}</div>
                        {(article.tags || []).slice(0, 2).map(tag => (
                          <span key={tag} className="badge" style={{ color: TAG_COLORS[tag] || 'var(--muted2)', borderColor: 'transparent', background: 'rgba(255,255,255,0.03)', fontSize: '0.6rem' }}>{tag}</span>
                        ))}
                      </div>
                      <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.93rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.4rem', lineHeight: '1.4' }}>{article.title}</div>
                      <div style={{ color: 'var(--muted)', fontSize: '0.82rem', lineHeight: '1.65' }}>{article.summary}</div>
                    </div>
                    <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted2)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {formatDate(article.published_at)}<br />
                      <span style={{ color: 'var(--neon-cyan)', marginTop: '0.3rem', display: 'block' }}>Read →</span>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {articles.length === 0 && (
            <div className="surface-panel" style={{ padding: '2rem' }}>
              <div className="feature-title">No articles found</div>
              <div className="stat-sub" style={{ marginTop: '0.4rem' }}>Try adjusting filters or clearing the search.</div>
            </div>
          )}
        </>
      )}

      {/* Source disclaimer */}
      <div className="card" style={{ background: 'rgba(0,0,0,0.2)', borderColor: 'var(--border)' }}>
        <div className="stat-label" style={{ marginBottom: '0.4rem' }}>About these articles</div>
        <div style={{ color: 'var(--muted)', fontSize: '0.8rem', lineHeight: '1.7' }}>
          Articles link to external sources — official government notifications, research organisations, and reputable news outlets. CarbonX does not own this content. Click any article to read the full source. Information may not reflect the latest regulatory position — always verify with official CCTS notifications.
        </div>
      </div>
    </div>
  );
}
